import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker, type ChevronProps } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

export type CalendarProps = React.ComponentProps<typeof DayPicker>

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: CalendarProps) {
  const navButtonClass =
    "inline-flex h-8 w-8 items-center justify-center rounded-full border border-border bg-bg-0 text-text-500 transition-all hover:border-brand-red hover:text-brand-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:opacity-40"

  return (
    <DayPicker
      fixedWeeks
      showOutsideDays={showOutsideDays}
      className={cn(
        "p-3 pointer-events-auto",
        "[&_.rdp-weekdays]:flex [&_.rdp-weekdays]:w-max [&_.rdp-weekdays]:justify-center",
        "[&_.rdp-week]:flex [&_.rdp-week]:w-max [&_.rdp-week]:justify-center",
        "[&_.rdp-weekday]:w-9 [&_.rdp-weekday]:p-0",
        "[&_.rdp-day]:w-9 [&_.rdp-day]:p-0",
        className
      )}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        month_caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium",
        nav: "space-x-1 flex items-center",
        button_previous: cn(navButtonClass, "absolute left-1"),
        button_next: cn(navButtonClass, "absolute right-1"),
        month_grid: "w-fit border-collapse space-y-1 mx-auto",
        weekdays: "flex w-full",
        weekday:
          "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        week: "flex w-full mt-2",
        day: "relative h-9 w-9 p-0 text-center text-sm transition-colors first:[&:has([aria-selected])]:rounded-l-lg last:[&:has([aria-selected])]:rounded-r-lg [&:has([aria-selected].day-range-end)]:rounded-r-lg [&:has([aria-selected])]:bg-rose-surface focus-within:relative focus-within:z-20",
        day_button: cn(
          buttonVariants({ variant: "ghost", size: "sm" }),
          "h-9 w-9 rounded-lg p-0 text-sm font-normal text-text-700 hover:bg-rose-surface hover:text-brand-red focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red/40 focus-visible:ring-offset-2 focus-visible:ring-offset-background aria-selected:opacity-100"
        ),
        range_end: "day-range-end",
        selected:
          "bg-brand-red text-white hover:bg-brand-red-600 focus:bg-brand-red focus:text-white",
        today:
          "border border-brand-red/30 bg-rose-surface text-brand-red",
        outside:
          "day-outside text-text-500 opacity-40 aria-selected:bg-rose-subtle aria-selected:text-brand-red aria-selected:opacity-50",
        disabled: "text-muted-foreground opacity-50",
        range_middle:
          "aria-selected:bg-rose-surface aria-selected:text-brand-red/90",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }: ChevronProps) =>
          orientation === "left" ? (
            <ChevronLeft className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          ),
      }}
      {...props}
    />
  )
}
Calendar.displayName = "Calendar"

export { Calendar }
